import type { GroupingRange } from "./table.types"

const MS_PER_DAY = 86_400_000

export const UNMATCHED_RANGE_LABEL = "Other"

/**
 * Returns the index of the first matching range for `value`, or `ranges.length`
 * if no range matches (the "Other" bucket).
 */
export function resolveRangeIndex(
  value: unknown,
  ranges: GroupingRange[],
): number {
  for (let i = 0; i < ranges.length; i++) {
    if (ranges[i].test(value)) return i
  }
  return ranges.length
}

/**
 * Returns the label for a given range index, falling back to "Other".
 */
export function resolveRangeLabel(
  index: number,
  ranges: GroupingRange[],
): string {
  return ranges[index]?.label ?? UNMATCHED_RANGE_LABEL
}

let _cachedInput: unknown = Symbol("unset")
let _cachedDays = 0

function daysAgo(value: unknown): number {
  if (value === _cachedInput) return _cachedDays
  _cachedInput = value

  if (value === null || value === undefined || value === "") {
    _cachedDays = Number.POSITIVE_INFINITY
    return _cachedDays
  }

  const ms = new Date(String(value)).getTime()
  if (Number.isNaN(ms)) {
    _cachedDays = Number.POSITIVE_INFINITY
    return _cachedDays
  }

  _cachedDays = (Date.now() - ms) / MS_PER_DAY
  return _cachedDays
}

/**
 * Pre-built ranges for relative date columns (e.g. "Last Sign-in").
 * Evaluated in order; first match wins.
 */
export const GROUPING_RANGE_DATES: GroupingRange[] = [
  { label: "Never", test: (v) => v === null || v === undefined || v === "" },
  { label: "Today", test: (v) => daysAgo(v) < 1 },
  { label: "Last 7 days", test: (v) => daysAgo(v) < 7 },
  { label: "Last 30 days", test: (v) => daysAgo(v) < 30 },
  { label: "Last 90 days", test: (v) => daysAgo(v) < 90 },
  { label: "Last year", test: (v) => daysAgo(v) < 365 },
  { label: "Over a year ago", test: () => true },
]
